"use client";

import { useState, useCallback } from "react";
import { ArrowRight } from "lucide-react";

interface EmailCaptureFormProps {
  /** Identifies where the form lives (e.g. "slitting-problems", "scrap-guide") */
  source: string;
  /** Headline above the input */
  headline?: string;
  /** Short description below the headline */
  description?: string;
  /** Button text */
  buttonText?: string;
  /** Optional extra metadata sent with the lead */
  metadata?: Record<string, unknown>;
}

export default function EmailCaptureForm({
  source,
  headline = "Get the full guide as PDF",
  description = "Enter your email and we'll send you a downloadable PDF with every technique in this article.",
  buttonText = "Send me the PDF",
  metadata,
}: EmailCaptureFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">(
    "idle"
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;

      setStatus("sending");
      try {
        const res = await fetch("/api/capture-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), source, metadata }),
        });

        if (!res.ok) throw new Error("Failed");
        setStatus("done");
      } catch {
        setStatus("error");
      }
    },
    [email, source, metadata]
  );

  if (status === "done") {
    return (
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6 text-center">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
          <svg
            className="w-5 h-5 text-emerald-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <p className="font-bold text-emerald-700 dark:text-emerald-300 mb-1">
          You&apos;re on the list!
        </p>
        <p className="text-sm text-emerald-600 dark:text-emerald-400">
          Check your inbox, we&apos;ll send the PDF shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-6 sm:p-8">
      <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2">
        {headline}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">
        {description}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d47723]/40 focus:border-[#d47723]"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="bg-[#d47723] hover:bg-[#c06a1f] disabled:opacity-60 text-white font-bold px-6 py-3 rounded-xl transition-all text-sm flex items-center justify-center gap-2 whitespace-nowrap shadow-lg shadow-orange-500/20"
        >
          {status === "sending" ? "Sending…" : buttonText}
          {status !== "sending" && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>
      {status === "error" && (
        <p className="text-xs text-red-500 mt-2">
          Something went wrong. Please try again.
        </p>
      )}
    </div>
  );
}
